import type { BlotterPanelTabType } from 'components/shared/Blotter/BlotterPanel/BlotterPanelHeader/BlotterPanelHeader';
import { DocumentationV3 } from 'epic/common-form/Documentation';
import type { BlotterItemAction } from 'models/blotters/BlotterItemAction';
import type { BlotterPanelField } from 'models/blotters/BlotterPanelField';
import type { BlotterType } from 'models/blotters/BlotterType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { BackendError } from 'models/NextWorkflowError';
import { selectTradesBlottersPageItems } from 'state/blotters/trades/tradesBlotters.selector';
import {
  getBlotterColumnDefinition,
  getBlotterType,
  getIsUserInternal,
  priceImprovementBlotter,
} from 'state/selectors';
import type { AppState } from 'state/state.model';

import type { BlotterPanelFieldType } from '../common/blotter.model';
import { getBlotterPanelFieldNames } from './blotterPanel.helpers';
import type { BlotterPanelIDByBlotterType, BlotterPanelState } from './blotterPanel.model';

function selectBlotterPanelState(appState: AppState): BlotterPanelState {
  return appState.blotters.blotterPanel;
}

export function getBlotterPanelItem(appState: AppState): IBlotterItem | undefined {
  const state = selectBlotterPanelState(appState);
  let blotterItems: readonly IBlotterItem[];
  switch (state.blotterType) {
    case 'quotes':
      return state.item;
    case 'trades':
    case 'products':
    case 'dlpProducts':
      return state.item;
    case 'dlpProductTrades':
      const productIsin = (state as BlotterPanelState<'dlpProductTrades'>).id?.productIsin;
      blotterItems = (
        productIsin ? selectTradesBlottersPageItems(appState, state.blotterType, productIsin) : []
      ) as readonly IBlotterItem[];
      break;
    case 'priceNegotiations':
      blotterItems = priceImprovementBlotter.getPageItems(appState) as readonly IBlotterItem[];
      break;
    default:
      blotterItems = [];
      break;
  }
  const item = blotterItems.find(item => {
    if (state.blotterType === 'dlpProductTrades') {
      const id = idSelector(state.blotterType, item);
      const stateId = (state as BlotterPanelState<'dlpProductTrades'>).id;
      return id?.productIsin === stateId?.productIsin && id?.tradeReference === stateId?.tradeReference;
    } else {
      return idSelector(state.blotterType, item) === state.id;
    }
  });
  return item;
}

function idSelector<Blotter extends BlotterType>(
  blotterType: Blotter,
  item?: IBlotterItem,
): BlotterPanelIDByBlotterType<Blotter> | undefined {
  switch (blotterType) {
    case 'quotes':
      return item?.quoteId as BlotterPanelIDByBlotterType<Blotter>;
    case 'trades':
      return item?.tradeId as BlotterPanelIDByBlotterType<Blotter>;
    case 'products':
      return item?.productId as BlotterPanelIDByBlotterType<Blotter>;
    case 'dlpProducts':
      return item?.isin as BlotterPanelIDByBlotterType<Blotter>;
    case 'dlpProductTrades':
      return item?.productIsin && item.tradeReference
        ? ({
            productIsin: item.productIsin,
            tradeReference: item.tradeReference,
          } as BlotterPanelIDByBlotterType<Blotter>)
        : undefined;
    default:
      return undefined;
  }
}

export function getBlotterPanelItemId(appState: AppState) {
  const state = selectBlotterPanelState(appState);
  if (state.blotterType !== 'dlpProductTrades') {
    return state.id as string;
  }
}

export function isBlotterPanelItemActive(appState: AppState): boolean {
  const item = getBlotterPanelItem(appState);
  return isFetchingBlotterPanelItemDetails(appState) || Boolean(item);
}

export function getBlotterPanelFields(appState: AppState): ReadonlyArray<BlotterPanelField> {
  const item = getBlotterPanelItem(appState);
  const state = selectBlotterPanelState(appState);
  if (item) {
    return computeBlotterFields(appState, state.blotterType);
  }
  return [];
}

function computeBlotterFields(state: AppState, blotterType: BlotterType): BlotterPanelField[] {
  const columnDefinitions = getBlotterColumnDefinition(state, blotterType);
  const fieldNames = getBlotterPanelFieldNames(blotterType);
  const fieldsNameToDisplay = fieldNames.filter(fieldName => {
    return columnDefinitions?.find(def => def.field === fieldName) !== undefined;
  });

  const blotterPanelFields = fieldsNameToDisplay.map(name => {
    const columnDefinition = columnDefinitions?.find(def => def.field === name);
    return {
      key: name,
      label: `blotterPanel.${name}`,
      type: <BlotterPanelFieldType>columnDefinition?.type,
    };
  });

  return blotterPanelFields;
}

export function getBlotterPanelTabs(state: AppState): BlotterPanelTabType[] {
  if (getIsUserInternal(state) && getBlotterType(state) === 'quotes') {
    const item = getBlotterPanelItem(state);
    if (item?.status === 'Quoted' && item?.pricerRfqId !== undefined) {
      return ['SUMMARY', 'MISSED_DEAL_FEEDBACK'];
    }
  }

  return ['SUMMARY'];
}
export function isBlotterPanelItemFetchingFailed(appState: AppState): boolean {
  const state = selectBlotterPanelState(appState);
  return state.isFetchingFailed;
}

export function getBlotterPanelItemFetchErrorDetails(appState: AppState): BackendError | undefined {
  const state = selectBlotterPanelState(appState);
  return state.fetchErrorDetails;
}

export function isBlotterPanelItemFetchingFailedShowAlert(appState: AppState): boolean {
  const state = selectBlotterPanelState(appState);
  return state.isShowFailedAlert;
}

export function isFetchingBlotterPanelItemDetails(appState: AppState): boolean {
  const state = selectBlotterPanelState(appState);
  return state.isFetching;
}

export function getBlotterPanelActions(appState: AppState): BlotterItemAction[] {
  const state = selectBlotterPanelState(appState);
  return state.actions ?? [];
}

export function getBlotterItemDocuments(appState: AppState): DocumentationV3 | undefined {
  const state = selectBlotterPanelState(appState);
  return state.documents;
}

export function getSecondaryTradeBlotterPanelSelected(state: AppState): string | undefined {
  if (state.blotters.blotterPanel.selected?.type === 'secondaryMarketTrades') {
    return state.blotters.blotterPanel.selected?.quoteId;
  }
}

export function getSecondaryOrderBlotterPanelSelected(state: AppState): string | undefined {
  if (state.blotters.blotterPanel.selected?.blotterType === 'orders') {
    return state.blotters.blotterPanel.selected?.id;
  }
}
